(function () {
  const ROOT_SELECTOR = '[data-newsletter-discount-popup]';
  const OPEN_CLASS = 'is-open';
  const BODY_OPEN_CLASS = 'dm-newsletter-discount-open';
  const DAY_IN_MS = 24 * 60 * 60 * 1000;
  const KLAVIYO_API_REVISION = '2026-07-15';
  const KLAVIYO_SUBSCRIPTIONS_ENDPOINT = 'https://a.klaviyo.com/client/subscriptions/';
  const BLOCKING_BODY_CLASSES = [
    'dm-customily-upload-processing-open',
    'dm-customily-photo-editor-open',
    'dm-customily-preview-open',
    'dm-customily-preview-pending',
    'dm-photo-guidelines-open',
    'dm-help-center-open',
    'menu-open'
  ];

  function safeLocalStorage() {
    try {
      const testKey = '__dm_newsletter_storage_test__';
      window.localStorage.setItem(testKey, '1');
      window.localStorage.removeItem(testKey);
      return window.localStorage;
    } catch (error) {
      return null;
    }
  }

  function safeSessionStorage() {
    try {
      const testKey = '__dm_newsletter_session_test__';
      window.sessionStorage.setItem(testKey, '1');
      window.sessionStorage.removeItem(testKey);
      return window.sessionStorage;
    } catch (error) {
      return null;
    }
  }

  function readCookie(name) {
    const prefix = `${encodeURIComponent(name)}=`;
    const cookie = document.cookie
      .split(';')
      .map((item) => item.trim())
      .find((item) => item.indexOf(prefix) === 0);

    return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : null;
  }

  function writeCookie(name, value, days) {
    const maxAge = Math.max(1, Math.round(days * 24 * 60 * 60));
    document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; path=/; max-age=${maxAge}; SameSite=Lax`;
  }

  function removeCookie(name) {
    document.cookie = `${encodeURIComponent(name)}=; path=/; max-age=0; SameSite=Lax`;
  }

  class NewsletterDiscountPopup {
    constructor(root) {
      this.root = root;
      this.enabled = root.dataset.enabled === 'true';
      this.delayMs = Math.max(0, Number(root.dataset.delaySeconds || 30) * 1000);
      this.resetDays = Math.max(1, Number(root.dataset.resetDays || 3));
      this.storageKey = root.dataset.storageKey || 'dearmora:newsletter-discount-popup:v1';
      this.visitKey = `${this.storageKey}:visit-start`;
      this.localStorage = safeLocalStorage();
      this.sessionStorage = safeSessionStorage();
      this.dialog = root.querySelector('[role="dialog"]');
      this.signupView = root.querySelector('[data-newsletter-signup-view]');
      this.successView = root.querySelector('[data-newsletter-success-view]');
      this.successEmail = root.querySelector('[data-newsletter-success-email]');
      this.successTitle = root.querySelector('[data-newsletter-success-view] h2');
      this.initialTitleId = this.dialog ? this.dialog.getAttribute('aria-labelledby') : '';
      this.successTitleId = this.dialog
        ? this.dialog.dataset.newsletterSuccessTitleId || ''
        : '';
      this.form = root.querySelector('form.dm-newsletter-popup__form');
      this.firstName = root.querySelector('input[name="first_name"]');
      this.email = root.querySelector('input[type="email"]');
      this.submitButton = this.form
        ? this.form.querySelector('button[type="submit"], input[type="submit"]')
        : null;
      this.closeButtons = Array.from(root.querySelectorAll('[data-newsletter-popup-close]'));
      this.klaviyoPublicApiKey = (root.dataset.klaviyoPublicApiKey || '').trim();
      this.klaviyoListId = (root.dataset.klaviyoListId || '').trim();
      this.klaviyoSource = (
        root.dataset.klaviyoSource || 'Dearmora newsletter discount popup'
      ).trim();
      this.klaviyoErrorMessage =
        root.dataset.klaviyoErrorMessage ||
        'We could not subscribe this email right now. Please try again.';
      this.timer = 0;
      this.retryTimer = 0;
      this.previousFocus = null;
      this.submitting = false;
      this.previewMode = new URLSearchParams(window.location.search).get('newsletter_popup_preview') === '1';
      this.onKeydown = this.onKeydown.bind(this);
      this.onCloseClick = this.onCloseClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);

      if (!this.enabled && !this.previewMode) return;

      this.closeButtons.forEach((button) => button.addEventListener('click', this.onCloseClick));
      if (this.form) this.form.addEventListener('submit', this.onSubmit);

      if (this.previewMode) {
        window.setTimeout(() => this.open(true), 150);
        return;
      }

      if (this.isDismissed()) return;
      this.schedule();
    }

    getDismissedAt() {
      const stored = this.localStorage
        ? this.localStorage.getItem(this.storageKey)
        : readCookie(this.storageKey);
      const timestamp = Number(stored);
      return Number.isFinite(timestamp) ? timestamp : 0;
    }

    isDismissed() {
      const dismissedAt = this.getDismissedAt();
      if (!dismissedAt) return false;

      if (Date.now() - dismissedAt < this.resetDays * DAY_IN_MS) return true;

      if (this.localStorage) this.localStorage.removeItem(this.storageKey);
      if (this.sessionStorage) this.sessionStorage.removeItem(this.visitKey);
      removeCookie(this.storageKey);
      return false;
    }

    rememberDismissal() {
      if (this.previewMode) return;
      const value = String(Date.now());
      if (this.localStorage) {
        this.localStorage.setItem(this.storageKey, value);
      } else {
        writeCookie(this.storageKey, value, this.resetDays);
      }
    }

    getVisitStartedAt() {
      if (!this.sessionStorage) return Date.now();

      const stored = Number(this.sessionStorage.getItem(this.visitKey));
      if (Number.isFinite(stored) && stored > 0) return stored;

      const now = Date.now();
      this.sessionStorage.setItem(this.visitKey, String(now));
      return now;
    }

    schedule() {
      window.clearTimeout(this.timer);
      const elapsed = Date.now() - this.getVisitStartedAt();
      const remaining = Math.max(0, this.delayMs - elapsed);
      this.timer = window.setTimeout(() => this.open(), remaining);
    }

    hasBlockingInterface() {
      if (BLOCKING_BODY_CLASSES.some((className) => document.body.classList.contains(className))) {
        return true;
      }

      return Boolean(
        document.querySelector(
          '#DmCustomilyUploadProcessingModal.is-open, #DmCustomilyPhotoEditorModal.is-open, .dm-customily-preview-modal.is-open, [role="dialog"][open]'
        )
      );
    }

    open(force) {
      if (!force && (this.isDismissed() || this.root.classList.contains(OPEN_CLASS))) return;

      if (!force && this.hasBlockingInterface()) {
        window.clearTimeout(this.retryTimer);
        this.retryTimer = window.setTimeout(() => this.open(), 2500);
        return;
      }

      this.previousFocus = document.activeElement;
      this.root.hidden = false;
      this.root.setAttribute('aria-hidden', 'false');
      document.body.classList.add(BODY_OPEN_CLASS);
      document.addEventListener('keydown', this.onKeydown);

      window.requestAnimationFrame(() => {
        this.root.classList.add(OPEN_CLASS);
        window.setTimeout(() => {
          if (this.firstName) {
            this.firstName.focus({ preventScroll: true });
          } else if (this.email) {
            this.email.focus({ preventScroll: true });
          } else if (this.closeButtons[0]) {
            this.closeButtons[0].focus({ preventScroll: true });
          }
        }, 120);
      });
    }

    close(options) {
      const settings = options || {};
      if (settings.remember !== false) this.rememberDismissal();

      window.clearTimeout(this.timer);
      window.clearTimeout(this.retryTimer);
      this.root.classList.remove(OPEN_CLASS);
      this.root.setAttribute('aria-hidden', 'true');
      document.body.classList.remove(BODY_OPEN_CLASS);
      document.removeEventListener('keydown', this.onKeydown);

      window.setTimeout(() => {
        this.root.hidden = true;
      }, 230);

      if (settings.restoreFocus !== false && this.previousFocus && this.previousFocus.focus) {
        this.previousFocus.focus({ preventScroll: true });
      }
    }

    onCloseClick() {
      this.close();
    }

    hasKlaviyoConfiguration() {
      return Boolean(this.klaviyoPublicApiKey && this.klaviyoListId);
    }

    setSubmitting(isSubmitting) {
      this.submitting = isSubmitting;
      if (!this.form) return;

      this.form.setAttribute('aria-busy', String(isSubmitting));
      if (this.submitButton) this.submitButton.disabled = isSubmitting;
    }

    getKlaviyoMessageElement() {
      if (!this.form) return null;

      let message = this.form.querySelector('[data-klaviyo-newsletter-message]');
      if (message) return message;

      message = document.createElement('small');
      message.className =
        'dm-newsletter-popup__message dm-newsletter-popup__message--error';
      message.dataset.klaviyoNewsletterMessage = '';
      message.setAttribute('role', 'alert');
      message.setAttribute('aria-live', 'polite');
      message.hidden = true;

      if (this.submitButton) {
        this.form.insertBefore(message, this.submitButton);
      } else {
        this.form.appendChild(message);
      }

      return message;
    }

    clearKlaviyoError() {
      const message = this.getKlaviyoMessageElement();
      if (!message) return;
      message.textContent = '';
      message.hidden = true;
    }

    showKlaviyoError(messageText) {
      const message = this.getKlaviyoMessageElement();
      if (!message) return;
      message.textContent = messageText || this.klaviyoErrorMessage;
      message.hidden = false;
    }

    showSuccess(email) {
      if (!this.signupView || !this.successView) {
        this.close({ remember: false });
        return;
      }

      this.signupView.hidden = true;
      this.successView.hidden = false;
      this.root.classList.add('is-success');

      if (this.successEmail) this.successEmail.textContent = email;
      if (this.dialog && this.successTitleId) {
        this.dialog.setAttribute('aria-labelledby', this.successTitleId);
      }

      window.requestAnimationFrame(() => {
        if (this.successTitle) {
          this.successTitle.focus({ preventScroll: true });
        } else {
          this.successView.focus({ preventScroll: true });
        }
      });
    }

    async subscribeToKlaviyo(email, firstName) {
      const endpoint = new URL(KLAVIYO_SUBSCRIPTIONS_ENDPOINT);
      endpoint.searchParams.set('company_id', this.klaviyoPublicApiKey);

      const response = await fetch(endpoint.toString(), {
        method: 'POST',
        headers: {
          accept: 'application/vnd.api+json',
          'content-type': 'application/vnd.api+json',
          revision: KLAVIYO_API_REVISION
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: this.klaviyoSource,
              profile: {
                data: {
                  type: 'profile',
                  attributes: {
                    email: email,
                    first_name: firstName,
                    subscriptions: {
                      email: {
                        marketing: {
                          consent: 'SUBSCRIBED'
                        }
                      }
                    },
                    properties: {
                      'Signup Source': this.klaviyoSource,
                      'Signup Page': window.location.href
                    }
                  }
                }
              }
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: this.klaviyoListId
                }
              }
            }
          }
        })
      });

      if (response.ok) return;

      let errorMessage = this.klaviyoErrorMessage;
      try {
        const payload = await response.json();
        const detail = payload && payload.errors && payload.errors[0]
          ? payload.errors[0].detail
          : '';
        if (detail) errorMessage = detail;
      } catch (error) {
        // Klaviyo can return an empty response body for some failures.
      }

      throw new Error(errorMessage);
    }

    async onSubmit(event) {
      if (!this.form || !this.email || !this.form.checkValidity()) return;

      if (this.submitting) {
        event.preventDefault();
        return;
      }

      if (!this.hasKlaviyoConfiguration()) {
        event.preventDefault();
        this.clearKlaviyoError();
        this.showKlaviyoError(this.klaviyoErrorMessage);
        console.error(
          '[Dearmora newsletter] Missing Klaviyo Public API Key or List ID. ' +
            'The form was not submitted.'
        );
        return;
      }

      event.preventDefault();
      this.clearKlaviyoError();
      this.setSubmitting(true);

      try {
        const submittedEmail = this.email.value.trim();
        const submittedFirstName = this.firstName
          ? this.firstName.value.trim()
          : '';
        await this.subscribeToKlaviyo(submittedEmail, submittedFirstName);
        this.rememberDismissal();
        this.form.reset();
        this.setSubmitting(false);
        this.showSuccess(submittedEmail);
      } catch (error) {
        this.showKlaviyoError(
          error instanceof Error && error.message
            ? error.message
            : this.klaviyoErrorMessage
        );
        this.setSubmitting(false);
      }
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab' || !this.dialog) return;

      const focusable = Array.from(
        this.dialog.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((element) => element.offsetParent !== null);

      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    openForPreview() {
      this.open(true);
    }

    clearDismissal() {
      if (this.localStorage) this.localStorage.removeItem(this.storageKey);
      if (this.sessionStorage) this.sessionStorage.removeItem(this.visitKey);
      removeCookie(this.storageKey);
    }

    destroy() {
      window.clearTimeout(this.timer);
      window.clearTimeout(this.retryTimer);
      this.closeButtons.forEach((button) => button.removeEventListener('click', this.onCloseClick));
      if (this.form) this.form.removeEventListener('submit', this.onSubmit);
      document.removeEventListener('keydown', this.onKeydown);
      document.body.classList.remove(BODY_OPEN_CLASS);
      this.root.classList.remove(OPEN_CLASS);
      this.root.classList.remove('is-success');
      if (this.signupView) this.signupView.hidden = false;
      if (this.successView) this.successView.hidden = true;
      if (this.successEmail) this.successEmail.textContent = '';
      if (this.dialog && this.initialTitleId) {
        this.dialog.setAttribute('aria-labelledby', this.initialTitleId);
      }
      this.root.hidden = true;
    }
  }

  function initializeNewsletterDiscountPopups(scope) {
    const roots = Array.from((scope || document).querySelectorAll(ROOT_SELECTOR));

    roots.forEach((root) => {
      if (root.dmNewsletterDiscountPopup) root.dmNewsletterDiscountPopup.destroy();
      root.dmNewsletterDiscountPopup = new NewsletterDiscountPopup(root);
    });

    window.DmNewsletterDiscountPopup = {
      instances: roots.map((root) => root.dmNewsletterDiscountPopup).filter(Boolean),
      openForPreview: function () {
        this.instances.forEach((instance) => instance.openForPreview());
      },
      clearDismissal: function () {
        this.instances.forEach((instance) => instance.clearDismissal());
      }
    };
  }

  function init() {
    initializeNewsletterDiscountPopups(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initializeNewsletterDiscountPopups(event.target);
  });
})();
